import React, { useContext, useEffect, useState } from "react";
import { EditableCheckbox } from "../../Atoms/inputs/EditableCheckbox";
import { EditableFormAttributeValueProperty } from "../../Atoms/EditableProperties/EditableFormAttributeValueProperty";
import { WindowContext } from "../../Service/Context/WindowContext";
import { DocumentService } from "../../Service/DocumentService";
import { FormatS } from "../../Service/FormatS";
import { Text } from "@mantine/core";
import { isDefined, printify } from "../../Utils/utils";
import { patchDocumentPosition } from "../../Service/restapi/sharedContractService";
import { useToasts } from "react-toast-notifications";
import { getDocumentTranslator } from "../Document/documentLanguage";
import { useLingui } from "@lingui/react";
import {
  clampPositionAmount,
  getMaxPositionAmount,
  getMinPositionAmount,
  isNegotiableAmountEditable,
  parsePositionAmount,
  shouldAutoSelectOptionalPosition,
  shouldHidePositionAmountAndPriceUnit,
} from "./positionDisplay";

// eslint-disable-next-line import/no-anonymous-default-export
export default ({
  _document,
  formSubmission,
  position,
  taxType,
  values,
  handleChange,
  patchSubmissionValue,
  columnStyles,
  onPositionChange,
}) => {
  const { windowWidth } = useContext(WindowContext);
  const { addToast } = useToasts();
  const { currency } = _document;
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, _document.language);
  const { discount = {}, priceUnit, price, netPrice, tax } = position;
  const { amount: discountAmount = 0 } = discount;
  const isFreeText = position.type === "FREE_TEXT";
  const minAmount = getMinPositionAmount(position.min);
  const maxAmount = getMaxPositionAmount(position.max);
  const [amount, setAmount] = useState(getInitialAmount(position.amount, minAmount, maxAmount));
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const negotiableAmountEditable = isNegotiableAmountEditable(
    position,
    _document.documentType
  );
  const hideAmountAndPriceUnit = shouldHidePositionAmountAndPriceUnit(
    position,
    _document.documentType
  );
  const firstColumnWidth = columnStyles.firstColumn.width;
  const priceColumnWidth = columnStyles.priceColumn.width;
  const showOptionalPositionColumn = firstColumnWidth > 0;
  const optionalPositionColumn = showOptionalPositionColumn
    ? `${firstColumnWidth}px `
    : "";
  const textColumnIndex = showOptionalPositionColumn ? 2 : 1;
  const totalColumnIndex =
    textColumnIndex + (windowWidth > 800 ? 3 : 1);
  const desktopGridColumns = `${optionalPositionColumn}minmax(0, 1fr) repeat(3, ${priceColumnWidth}px)`;
  const compactGridColumns = `${optionalPositionColumn}minmax(0, 1fr) ${priceColumnWidth}px`;

  useEffect(() => {
    setAmount(getInitialAmount(position.amount, minAmount, maxAmount));
  }, [position.amount, minAmount, maxAmount]);

  let styles = {
    container: {
      display: "grid",
      gridTemplateColumns: isFreeText
        ? `${optionalPositionColumn}minmax(0, 1fr)`
        : windowWidth > 800
          ? desktopGridColumns
          : compactGridColumns,
      padding: isFreeText ? "14px 0" : "12px 0",
    },
    label: {
      fontWeight: isFreeText ? 600 : 400,
      color: "#30343a",
      lineHeight: 1.4,
      overflowWrap: "break-word",
      wordWrap: "break-word",
    },
    subtext: {
      marginTop: 4,
      color: "#6d6d6d",
      fontSize: 13,
      lineHeight: 1.45,
    },
    firstColumn: {
      ...columnStyles.firstColumn,
    },
    textColumn: {
      ...columnStyles.textColumn,
      gridColumn:
        !isFreeText && windowWidth > 800 && hideAmountAndPriceUnit
          ? `${textColumnIndex} / ${totalColumnIndex}`
          : undefined,
    },
    priceColumn: {
      ...columnStyles.priceColumn,
    },
    totalColumn: {
      ...columnStyles.priceColumn,
      gridColumn: totalColumnIndex,
    },
    amountInput: {
      width: 74,
      maxWidth: 74,
      marginLeft: "auto",
    },
    amountInputWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
    },
  };

  let patchField = position.customAttributeId
    ? position.customAttributeId
    : position.id;
  let keys = [];
  let obj;

  for (obj in values) {
    if (values[obj]) {
      keys.push(obj);
    }
  }

  if (isFreeText) {
    return (
      <div style={styles.container}>
        {showOptionalPositionColumn && <div style={styles.firstColumn} />}
        <div style={styles.textColumn}>
          <div style={styles.label}>
            <div dangerouslySetInnerHTML={{ __html: position.label }} />
          </div>
          <div
            className={"description"}
            style={styles.subtext}
            dangerouslySetInnerHTML={{ __html: position.subtext }}
          />
        </div>
      </div>
    );
  }

  const commitAmount = (newAmount) => {
    const requestedAmount = parsePositionAmount(newAmount);
    const nextAmount = clampPositionAmount(newAmount, minAmount, maxAmount);
    const shouldSelectPosition = shouldAutoSelectOptionalPosition(
      position,
      nextAmount,
      values[patchField]
    );

    const selectPosition = () => {
      if (
        !shouldSelectPosition ||
        typeof patchSubmissionValue !== "function"
      ) {
        return Promise.resolve();
      }

      return patchSubmissionValue(patchField, true);
    };

    setAmount(nextAmount);

    if (isDefined(maxAmount) && requestedAmount > maxAmount) {
      addToast(`Die angefragte Menge übersteigt die verfügbare Kapazität. Für diese Position können maximal ${maxAmount} bereitgestellt werden.`, {
        appearance: "info",
        autoDismiss: true,
      });
    }

    if (`${nextAmount}` === `${position.amount}` || !_document || !_document.id) {
      selectPosition().catch((error) => {
        setAmountError(true);
        console.error("could not select optional document position", error);
      });
      return;
    }

    setAmountLoading(true);
    setAmountError(false);

    patchDocumentPosition(_document.id, position.id, {
      amount: `${nextAmount}`,
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        throw resp;
      })
      .then((updatedPosition) => {
        if (typeof onPositionChange === "function") {
          onPositionChange(updatedPosition);
        }

        return selectPosition();
      })
      .catch((error) => {
        setAmountError(true);
        console.error("could not patch document position amount", error);
      })
      .finally(() => setAmountLoading(false));
  };

  const renderAmount = () => {
    if (!negotiableAmountEditable) {
      return (
        <>
          {position.amount} {position.unit}
        </>
      );
    }

    return (
      <div style={styles.amountInputWrapper}>
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]+([.,][0-9]+)?"
          value={amount}
          disabled={formSubmission.lastSubmitted}
          onChange={(event) => setAmount(event.currentTarget.value)}
          onBlur={(event) => commitAmount(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
          style={{
            ...styles.amountInput,
            textAlign: "right",
            height: 28,
            minHeight: 28,
            paddingLeft: 8,
            paddingRight: 4,
            borderColor: amountError ? "red" : undefined,
          }}
          className={`form-control${amountLoading ? " loading" : ""}`}
        />
        {isDefined(position.unit) && (
          <Text size="sm">{printify(position.unit)}</Text>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={styles.container}>
        {showOptionalPositionColumn && (
          <div style={styles.firstColumn}>
            {position.optional && (
              <EditableFormAttributeValueProperty
                formSubmissionId={formSubmission.id}
                patchField={patchField}
                changeHandler={(p) => {
                  handleChange(patchField, p[patchField]);
                }}
                entity={values}
                values={keys}
                labelValue={position.id + ""}
                editFormatter={EditableCheckbox}
                multiple={false}
                editOnly={true}
                disabled={formSubmission.lastSubmitted}
              />
            )}
          </div>
        )}
        <div style={styles.textColumn}>
          <div style={styles.label}>
            <div dangerouslySetInnerHTML={{ __html: position.label }} />
          </div>
          <div
            className={"description"}
            style={styles.subtext}
            dangerouslySetInnerHTML={{ __html: position.subtext }}
          />
        </div>
        {windowWidth > 800 && !hideAmountAndPriceUnit && (
          <div style={styles.priceColumn}>
            {renderAmount()}
          </div>
        )}
        {windowWidth > 800 && !hideAmountAndPriceUnit && (
          <div style={styles.priceColumn}>
            {isDefined(priceUnit) && (
              <div>
                {FormatS.formatPriceWithCurrency(
                  currency,
                  DocumentService.calculateNetGrossPrice(
                    taxType,
                    priceUnit,
                    tax
                  )
                )}
              </div>
            )}
          </div>
        )}
        <div style={styles.totalColumn}>
          <div>
            {windowWidth < 800 &&
              !hideAmountAndPriceUnit &&
              (negotiableAmountEditable ? (
                renderAmount()
              ) : (
                !(isAmountOne(position.amount) && !isDefined(position.unit)) && (
                  <Text size="xs" color="dimmed">{`${position.amount} ${printify(
                    position.unit
                  )}`}</Text>
                )
              ))}
            <b>
              {FormatS.formatPriceWithCurrency(
                currency,
                taxType === "net" ? netPrice : price
              )}
            </b>
          </div>
          {discountAmount > 0 && (
            <Text size="xs">{`${t("discount")}: ${FormatS.formatPriceWithCurrency(
              currency,
              discountAmount
            )}`}</Text>
          )}
        </div>
      </div>
    </>
  );
};

const getInitialAmount = (amount, minAmount, maxAmount) => {
  return clampPositionAmount(amount, minAmount, maxAmount);
};

const isAmountOne = (amount) => {
  return Number(amount) === 1;
};
